import { Check, ChevronDown } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export function CustomSelect({ value, options, onChange, placeholder = 'Выберите значение', ariaLabel, className = '', disabled = false }: {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  className?: string
  disabled?: boolean
}) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 220 })
  const selected = useMemo(() => options.find(option => option.value === value), [options, value])

  const placeMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const expectedHeight = Math.min(280, options.length * 41 + 10)
    const below = rect.bottom + 6
    const top = below + expectedHeight > window.innerHeight - 12 ? Math.max(12, rect.top - expectedHeight - 6) : below
    setPosition({ top, left: rect.left, width: rect.width })
  }

  const show = () => {
    if (disabled) return
    placeMenu()
    const selectedIndex = options.findIndex(option => option.value === value)
    setActiveIndex(Math.max(0, selectedIndex))
    setOpen(true)
  }

  const choose = (option: SelectOption) => {
    if (option.disabled) return
    onChange(option.value)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!open && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault(); show(); return
    }
    if (!open) return
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); return }
    if (options.length === 0) return
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(options[activeIndex]); return }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      let next = activeIndex
      do { next = (next + direction + options.length) % options.length } while (options[next]?.disabled && next !== activeIndex)
      setActiveIndex(next)
    }
  }

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      const target = event.target as Node
      if (!buttonRef.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false)
    }
    const reposition = () => placeMenu()
    document.addEventListener('mousedown', close)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('mousedown', close)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, options.length])

  return <div className={`custom-select ${open ? 'is-open' : ''} ${className}`}>
    <button ref={buttonRef} type="button" className="custom-select-trigger" role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} disabled={disabled} onClick={() => open ? setOpen(false) : show()} onKeyDown={onKeyDown}>
      <span className={selected ? '' : 'placeholder'}>{selected?.label ?? placeholder}</span><ChevronDown/>
    </button>
    {open && createPortal(<div ref={listRef} className="custom-select-menu" role="listbox" aria-label={ariaLabel} style={position}>
      {options.length === 0 && <div className="custom-select-empty">Нет доступных вариантов</div>}
      {options.map((option, index) => <button type="button" role="option" aria-selected={option.value === value} disabled={option.disabled} key={option.value} className={`${option.value === value ? 'selected' : ''} ${activeIndex === index ? 'active' : ''}`} onMouseEnter={() => setActiveIndex(index)} onClick={() => choose(option)}><span>{option.label}</span>{option.value === value && <Check/>}</button>)}
    </div>, document.body)}
  </div>
}
